/**
 * Pathless auth-gate layout — wraps every authenticated /w/{slug}/...
 * route (formerly /dashboard/...). Replaces the old _dashboard.tsx as
 * part of the slug-prefix URL refactor (2026-06-03).
 *
 * Responsibilities (intentionally narrow):
 *  - block unauthenticated callers → bounce to /login?next=<current>
 *  - block a session still pending 2FA (password accepted, second factor
 *    not given — /me carries `pending`) → bounce to /2fa-verify?next=…
 *  - block an enrolment-only session (2FA became mandatory and there is
 *    no method — /me carries `enrollment_required`) → /2fa-setup?required=1
 *  - block identity-only callers (authenticated, no active membership)
 *    → bounce to /select-workspace?next=<current>
 *  - show a full-screen spinner while /me is still in flight
 *
 * Sidebar, topbar, ModuleNavProvider, DPDP modal — all of that moved
 * to the tenant-scoped layout at `_app/w.$tenantSlug.tsx`, where it
 * has access to the slug param and can verify the URL slug matches
 * the active /me.tenant.slug.
 *
 * Why split? The auth gate doesn't care about which workspace; it just
 * gates "is anyone logged in." The chrome cares about WHICH workspace.
 * Keeping them separate means /select-workspace can't accidentally
 * inherit a half-rendered topbar pointing at a stale slug.
 */

import { useEffect } from "react";
import {
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { safeNext } from "@/lib/auth/safe-next";
import { authGateTarget, isGatedPath } from "@/lib/auth/auth-gate";

export const Route = createFileRoute("/_authgated")({
  component: AuthGate,
});

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-7 w-7 animate-spin text-primary" />
    </div>
  );
}

function AuthGate() {
  const { user, loading, identityOnly } = useAuth();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const search = useLocation({ select: (l) => l.search });

  // Guard logout flicker — see legacy _dashboard.tsx comment: during
  // sign-out the user-menu navigates to /login first, then auth flips
  // to null on the same tick. Without the prefix guard we'd re-bounce
  // already-on-/login pages, compounding ?next= forever.
  useEffect(() => {
    if (loading) return;
    if (!isGatedPath(pathname)) return;
    const here = (() => {
      const params = new URLSearchParams(
        Object.entries(search ?? {}).flatMap(([k, v]) =>
          v === undefined || v === null ? [] : [[k, String(v)]],
        ),
      );
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    })();
    const next = safeNext(here);
    // lib/auth/auth-gate.ts owns the order: no session → /login; pending
    // 2FA → /2fa-verify (every tenant route answers 401 totp_required and
    // the step-up prompt lives in the workspace layout this gate has not
    // mounted; before the rotation, /change-password is not open to a
    // pending session); temporary password → /change-password (099);
    // enrolment-only (2FA became mandatory mid-session) → /2fa-setup
    // ?required=1; identity-only → /select-workspace.
    const target = authGateTarget(user);
    if (target === null) return;
    if (target === "/2fa-setup") {
      void navigate({ to: "/2fa-setup", search: { required: 1, next }, replace: true });
      return;
    }
    void navigate({ to: target, search: { next }, replace: true });
  }, [loading, user, identityOnly, pathname, search, navigate]);

  if (loading || authGateTarget(user) !== null || identityOnly) {
    return <FullScreenSpinner />;
  }
  return <Outlet />;
}
